import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonDatetime,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import {useParams} from "react-router-dom";
import React, {useEffect, useState, useCallback, useMemo} from "react";
import './AdminPersonalOverview.css'
import createAxiosInstance from "../../services/AxiosInstance";
import {Transaction} from "../../model/Transaction";
import {User} from "../../model/User";
import { useToast } from "../../utils/useToasts";
import TransactionCard from "../../components/transactions/TransactionCard";
import ProfilePicture from "../../components/shared/ProfilePicture";
import TagsInput from "../../components/shared/TagsInput";
import {APP_ROLES} from "../../auth/rbac";

const METHOD_NOT_ALLOWED_STATUS = 405;
const ISO_DATE_SLICE_END = 10;

const isNumericRouteParam = (value?: string): boolean => {
    return typeof value === 'string' && /^\d+$/.test(value);
};

const getTodayDateString = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeLocalDateParam = (value: string): string | null => {
    if (!value) return null;

    const directMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (directMatch) {
        return directMatch[0];
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const AdminPersonalOverview: React.FC = () => {

    const [user, setUser] = useState<User>();
    const [transactions, setTransactions] = useState<Transaction[]>();
    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [remark, setRemarks] = useState<string>('Opwaarderen')
    const {playerId} = useParams<{ playerId?: string }>();
    const [newTransactionAmount, setNewTransactionAmount] = useState<number>(0)
    const [selectedDate, setSelectedDate] = useState<string>(getTodayDateString());
    const [isManualCheckinOpen, setIsManualCheckinOpen] = useState<boolean>(false);
    const showToasts = useToast();
    const axios = useMemo(() => createAxiosInstance(), []);

    const availableRoles = useMemo(() => [...APP_ROLES], []);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [blackList, setBlackList] = useState<boolean>(false);

    const fetchUserData = useCallback(() => {
        if (!isNumericRouteParam(playerId)) {
            return;
        }

        axios.get(`/user/${playerId}`)
            .then(res => {
                setUser(res.data)
                setCurrentBalance(res.data.balance)
                setTransactions(Array.isArray(res.data.transactions) ? [...res.data.transactions].reverse() : [])
                setSelectedRoles(res.data.roles || [])
                setBlackList(!!res.data.blackList);
            })
    }, [axios, playerId]);
    const handleBlacklistToggle = (e: CustomEvent) => {
        const newValue = e.detail.checked;
        setBlackList(newValue);
        axios.patch(`/user/${playerId}/blacklist`, { blackList: newValue })
            .then(res => {
                setUser(res.data);
                showToasts('Blacklist status updated', 'success');
            })
            .catch(() => {
                setBlackList(user?.blackList ?? false);
                showToasts('Failed to update blacklist status', 'danger');
            });
    };

    const updateRoles = useCallback((updatedRoles: string[]) => {
        if (!isNumericRouteParam(playerId)) {
            showToasts('Geen geldige speler geselecteerd', 'danger');
            return;
        }

        setSelectedRoles(updatedRoles);
        axios.patch(`/user/${playerId}/roles`, { roles: updatedRoles })
            .then(res => {
                console.log('Roles updated successfully:', res.data);
                setUser(res.data);
                showToasts('Rollen zijn bijgewerkt', 'success');
            })
            .catch(() => {
                setSelectedRoles(user?.roles || []);
                showToasts('Er is iets mis gegaan bij het bijwerken van rollen', 'danger');
            });
    }, [axios, playerId, showToasts, user?.roles]);

    const keyPress = (event: { key: string; }) => {
        if (event.key === 'Enter' && newTransactionAmount !== 0) {

            const newTransaction: Transaction = {
                deltaBalance: newTransactionAmount,
                dateOfTransaction: new Date(),
                remark,
                isCheckIn: false
            }

            axios.post(`/transactions/${playerId}`, newTransaction)
                .then(res => {
                    fetchUserData()
                    showToasts(`€ ${res.data.deltaBalance} is opgewaardeerd!`, 'success')
                })
                .finally(() => {
                    setNewTransactionAmount(0);
                    setRemarks("Opwaarderen");
                })
        }
    }

    const handleSave = () => {
        const normalizedDate = normalizeLocalDateParam(selectedDate);

        if (!normalizedDate) {
            showToasts('Selecteer eerst een datum!', 'danger')
            return;
        }

        if (!isNumericRouteParam(playerId)) {
            showToasts('Geen speler geselecteerd', 'danger');
            return;
        }

        axios.post('/evening/check-in-manually', null, {
            params: {
                userId: playerId,
                date: normalizedDate
            }
        })
            .then(res => {
                fetchUserData()
                showToasts(`${res.data.name} is op ${res.data.checkInDate} ingecheckt!`, 'success')
                setIsManualCheckinOpen(false);
            })
            .catch(error => {
                if (error.request.status === METHOD_NOT_ALLOWED_STATUS) {
                    showToasts('Al ingecheckt', 'danger')
                } else {
                    showToasts(`Er is iets mis gegaan, laat Ties de tering zooi maar weer uitzoeken`, 'danger')
                }
            })
    };

    useEffect(() => {
        fetchUserData()
    }, [fetchUserData])

    const getItemLabel = useCallback((r: string) => r, []);
    const getItemKey = useCallback((r: string) => r, []);
    const handleDateChange = (nextValue: string | string[] | null | undefined) => {
        if (typeof nextValue === 'string') {
            setSelectedDate(nextValue.slice(0, ISO_DATE_SLICE_END));
            return;
        }

        if (Array.isArray(nextValue) && nextValue.length > 0 && typeof nextValue[0] === 'string') {
            setSelectedDate(nextValue[0].slice(0, ISO_DATE_SLICE_END));
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton/>
                    </IonButtons>
                    <IonTitle>Saldo aanpassen</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent>
                <IonCard className='user-card'>
                    <IonCardHeader>
                        <div className={'user-container'}>
                            {user && <ProfilePicture profilePicUrl={(user.profilePictureUrl?.length !== 0 && user.profilePictureUrl) ? `${process.env.REACT_APP_STORAGE}${user.profilePictureUrl}` : `${process.env.REACT_APP_STORAGE}default-profile.jpg`}
                                                     height={50}
                                                     width={50}
                                                     showEditable={false}
                                                     fullName={user.name}
                            />
                            }
                            <div className={'user-info-container'}>
                                <IonCardTitle class='user-card-header'>{user?.name}</IonCardTitle>
                                <IonNote class='user-card-header'>Saldo: € {currentBalance}</IonNote>
                                <IonNote class='user-card-header'>Email: {user?.emailAddress != null ? user?.emailAddress : "Geen email ingevuld"}</IonNote>
                            </div>
                        </div>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonItem>
                            <IonLabel>Saldo mutatie:</IonLabel>
                            <IonInput value={newTransactionAmount} onKeyPress={keyPress}
                                      onIonInput={(e: any) => setNewTransactionAmount(e.target.value!)}
                                      placeholder="Hoeveelheid geld"
                                      clearInput type="number"/>
                        </IonItem>
                        <IonItem>
                            <IonLabel>Opmerking:</IonLabel>
                            <IonInput value={remark} placeholder="Opmerking"
                                      onKeyPress={keyPress}
                                      onIonInput={(e: any) => setRemarks(e.target.value!)}
                                      clearInput/>
                        </IonItem>
                    </IonCardContent>
                    <IonCardContent>
                        <IonItem className="blacklist-item-space">
                            <IonLabel>Zwarte lijst</IonLabel>
                            <input
                                type="checkbox"
                                checked={blackList}
                                onChange={e => {
                                    const newValue = e.target.checked;
                                    setBlackList(newValue);
                                    axios.patch(`/user/${playerId}/blacklist`, { blackList: newValue })
                                        .then(res => {
                                            setUser(res.data);
                                            showToasts('Zwarte lijst status bijgewerkt', 'success');
                                        })
                                        .catch(() => {
                                            setBlackList(user?.blackList ?? false);
                                            showToasts('Mislukt bijwerken van zwarte lijst status', 'danger');
                                        });
                                }}
                                style={{ width: 22, height: 22 }}
                            />
                        </IonItem>
                    </IonCardContent>
                </IonCard>

                <IonCard>
                    <IonCardContent>
                        <TagsInput
                             selectedItems={selectedRoles}
                             onSelectedItemsChange={updateRoles}
                             availableItems={availableRoles}
                             getItemLabel={getItemLabel}
                             getItemKey={getItemKey}
                             label="Rollen"
                             placeholder="Zoek een rol..."
                             emptyStateText="Geen rollen geselecteerd"
                             noResultsText="Geen rollen gevonden"
                             maxSuggestions={6}
                             debounceMs={200}
                             portal={true}
                         />
                    </IonCardContent>
                </IonCard>

                <IonCard className="manual-checkin-card">
                    <IonCardHeader>
                        <div className="manual-checkin-header">
                            <div>
                                <IonCardTitle>Handmatige check-in</IonCardTitle>
                                <IonNote className="manual-checkin-date-preview">Geselecteerd: {selectedDate}</IonNote>
                            </div>
                            <IonButton
                                fill="outline"
                                size="small"
                                className="manual-checkin-toggle"
                                onClick={() => setIsManualCheckinOpen(prev => !prev)}
                            >
                                {isManualCheckinOpen ? 'Inklappen' : 'Datum kiezen'}
                            </IonButton>
                        </div>
                    </IonCardHeader>
                    {isManualCheckinOpen && (
                        <IonCardContent>
                            <div className="manual-checkin-controls">
                                <div className="manual-checkin-datetime-wrap">
                                    <IonDatetime
                                        className="manual-checkin-datetime"
                                        value={selectedDate}
                                        presentation="date"
                                        firstDayOfWeek={1}
                                        locale="nl-NL"
                                        onIonChange={(event) => handleDateChange(event.detail.value)}
                                    />
                                </div>
                                <IonButton onClick={handleSave}>Check in</IonButton>
                            </div>
                        </IonCardContent>
                    )}
                </IonCard>

                <IonList>
                    {transactions?.map((value: Transaction, key: any) => (
                        <TransactionCard key={key} amount={value.deltaBalance} description={value.remark}
                                         date={value.dateOfTransaction} isTransaction={!value.isCheckIn}/>
                    ))}
                </IonList>
            </IonContent>
        </IonPage>

    );
}

export default AdminPersonalOverview;
